import type { Ref } from 'vue'
import debounce from 'lodash/debounce'

interface SearchSuggestion {
  id: string
  text: string
  type: 'KEYWORD' | 'CATEGORY' | 'BRAND' | 'VENDOR'
  source_id: number | null
  weight: number
  metadata: {
    url: string
    image?: string
    logo?: string
    breadcrumb?: string
  }
  score?: number
  match_percentage?: number
  count?: number
}

interface SearchResponse {
  data: {
    suggestions: SearchSuggestion[]
    products?: {
      categories?: SearchSuggestion[]
      brands?: SearchSuggestion[]
    }
  }
}

interface UseLiveSearchReturn {
  suggestions: Ref<SearchSuggestion[]>
  productCategories: Ref<SearchSuggestion[]>
  productBrands: Ref<SearchSuggestion[]>
  isLoading: Ref<boolean>
  selectedIndex: Ref<number>
  search: (query: string) => Promise<void>
  handleKeyDown: (event: KeyboardEvent) => void
  reset: () => void
  highlightMatch: (text: string, query: string) => string
  getIconForType: (type: SearchSuggestion['type']) => string
  navigateToSuggestion: (suggestion: SearchSuggestion) => void
}

export const useLiveSearch = (): UseLiveSearchReturn => {
  const { API_ENDPOINTS } = useApiEndpoints()
  const router = useRouter()
  const suggestions = ref<SearchSuggestion[]>([])
  const productCategories = ref<SearchSuggestion[]>([])
  const productBrands = ref<SearchSuggestion[]>([])
  const isLoading = ref(false)
  const selectedIndex = ref(-1)
  const abortController = ref<AbortController | null>(null)
  const cache = new Map<string, { 
    data: SearchSuggestion[], 
    categories: SearchSuggestion[], 
    brands: SearchSuggestion[],
    timestamp: number 
  }>()
  const CACHE_SIZE = 10
  const CACHE_TTL = 5 * 60 * 1000 // 5 dakika (milisaniye cinsinden)

  // Arama anahtar kelimesi oluşturma yardımcı fonksiyonu
  const createSearchKeyword = (query: string): SearchSuggestion => ({
    id: 'search-keyword',
    text: query,
    type: 'KEYWORD',
    source_id: null,
    weight: 1000,
    metadata: {
      url: `/katalog/arama?q=${encodeURIComponent(query)}`
    },
    score: 1,
    match_percentage: 100
  })

  // Türkçe karakterleri normalize etme yardımcı fonksiyonu
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[iıİI]/g, 'i')
      .replace(/[öÖ]/g, 'o')
      .replace(/[üÜ]/g, 'u')
      .replace(/[şŞ]/g, 's')
      .replace(/[çÇ]/g, 'c')
      .replace(/[ğĞ]/g, 'g')
  }

  // Arama fonksiyonu
  const search = async (query: string): Promise<void> => {
    if (!query) {
      suggestions.value = []
      productCategories.value = []
      productBrands.value = []
      return
    }

    // Aranan kelimeyi her zaman ekle
    const searchKeyword = createSearchKeyword(query)

    // Tek harf yazıldığında sadece aranan kelimeyi göster, API'ye istek atmadan
    if (query.length < 2) {
      suggestions.value = [searchKeyword]
      productCategories.value = []
      productBrands.value = []
      return
    }

    // Önbellekten kontrol - zaman aşımını da kontrol ediyoruz
    const cachedItem = cache.get(query)
    const now = Date.now()
    if (cachedItem && (now - cachedItem.timestamp) < CACHE_TTL) {
      suggestions.value = cachedItem.data
      productCategories.value = cachedItem.categories
      productBrands.value = cachedItem.brands
      return
    }

    // Önceki isteği iptal et
    if (abortController.value) {
      abortController.value.abort()
    }
    
    // Yeni AbortController oluştur
    abortController.value = new AbortController()
    const currentController = abortController.value
    
    isLoading.value = true

    try {
      // API isteği yap
      const response = await useBaseOFetchWithAuth(API_ENDPOINTS.public.products.liveSearch(query), {
        signal: currentController.signal
      }).catch(() => null) as SearchResponse | null
      
      // İstek iptal edildiyse veya controller değiştiyse işlemi durdur
      if (currentController !== abortController.value || currentController.signal.aborted) {
        return
      }
      
      if (response?.data) {
        // Suggestions verilerini işle
        if (response.data.suggestions) {
          suggestions.value = [searchKeyword, ...response.data.suggestions].slice(0, 5)
        } else {
          suggestions.value = [searchKeyword]
        }
        
        // Products altındaki categories ve brands verilerini işle
        if (response.data.products?.categories) {
          productCategories.value = response.data.products.categories
        } else {
          productCategories.value = []
        }
        
        if (response.data.products?.brands) {
          productBrands.value = response.data.products.brands
        } else {
          productBrands.value = []
        }
        
        // Önbelleğe ekle - zaman damgası ile birlikte
        cache.set(query, { 
          data: suggestions.value,
          categories: productCategories.value,
          brands: productBrands.value,
          timestamp: Date.now()
        })
        
        if (cache.size > CACHE_SIZE) {
          const firstKey = Array.from(cache.keys())[0]
          if (firstKey) {
            cache.delete(firstKey)
          }
        }
      } else {
        // API yanıt vermezse sadece aranan kelimeyi göster
        suggestions.value = [searchKeyword]
        productCategories.value = []
        productBrands.value = []
      }
    } catch (error) {
      // Tüm hataları sessizce geç, sadece aranan kelimeyi göster
      suggestions.value = [searchKeyword]
      productCategories.value = []
      productBrands.value = []
    } finally {
      // İstek iptal edilmediyse loading'i kapat
      if (currentController === abortController.value && !currentController.signal.aborted) {
        isLoading.value = false
      }
    }
  }

  // Debounce edilmiş arama
  const debouncedSearch = debounce(search, 300)

  // Klavye navigasyonu
  const handleKeyDown = (event: KeyboardEvent) => {
    // Tüm sonuçları birleştir (suggestions + categories + brands)
    const allResults = [
      ...suggestions.value,
      ...productCategories.value,
      ...productBrands.value
    ]
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        selectedIndex.value = Math.min(
          selectedIndex.value + 1,
          allResults.length - 1
        )
        break
      case 'ArrowUp':
        event.preventDefault()
        selectedIndex.value = Math.max(selectedIndex.value - 1, -1)
        break
      case 'Enter':
        event.preventDefault()
        if (selectedIndex.value >= 0 && selectedIndex.value < allResults.length) {
          navigateToSuggestion(allResults[selectedIndex.value])
        }
        break
      case 'Escape':
        event.preventDefault()
        reset()
        break
    }
  }

  // Sıfırlama fonksiyonu
  const reset = () => {
    suggestions.value = []
    productCategories.value = []
    productBrands.value = []
    selectedIndex.value = -1
    isLoading.value = false
    if (abortController.value) {
      abortController.value.abort()
      abortController.value = null
    }
  }

  // Eşleşen metni vurgulama
  const highlightMatch = (text: string, query: string): string => {
    if (!query) return text

    const normalizedText = normalizeText(text)
    const normalizedQuery = normalizeText(query)

    const index = normalizedText.indexOf(normalizedQuery)
    if (index === -1) return text

    return (
      text.slice(0, index) +
      '<strong>' +
      text.slice(index, index + query.length) +
      '</strong>' +
      text.slice(index + query.length)
    )
  }

  // Öneri tipine göre ikon
  const getIconForType = (type: SearchSuggestion['type']): string => {
    switch (type) {
      case 'KEYWORD':
        return 'i-heroicons-magnifying-glass'
      case 'CATEGORY':
        return 'i-heroicons-squares-2x2'
      case 'BRAND':
        return 'i-heroicons-check-badge'
      case 'VENDOR':
        return 'i-heroicons-building-storefront'
      default:
        return 'i-heroicons-magnifying-glass'
    }
  }

  // Öneriye yönlendirme
  const navigateToSuggestion = (suggestion: SearchSuggestion) => {
    if (suggestion.metadata?.url) {
      router.push(suggestion.metadata.url)
    } else {
      switch (suggestion.type) {
        case 'KEYWORD':
          router.push(`/arama?q=${encodeURIComponent(suggestion.text)}`)
          break
        case 'CATEGORY':
          if (suggestion.source_id) {
            router.push(`/kategoriler/${suggestion.source_id}`)
          }
          break
        case 'BRAND':
          if (suggestion.source_id) {
            router.push(`/markalar/${suggestion.source_id}`)
          }
          break
        case 'VENDOR':
          if (suggestion.source_id) {
            router.push(`/satici/${suggestion.source_id}`)
          }
          break
      }
    }
    reset()
  }

  return {
    suggestions,
    productCategories,
    productBrands,
    isLoading,
    selectedIndex,
    search: debouncedSearch as (query: string) => Promise<void>,
    handleKeyDown,
    reset,
    highlightMatch,
    getIconForType,
    navigateToSuggestion
  }
} 